import { Popover, Text } from '@byecode/ui'
import type { DataSourceAbstract } from '@lighthouse/core'
import { ActionItemPattern, BACKGROUND_TYPE } from '@lighthouse/core'
import type { VariableTree2 } from '@lighthouse/shared'
import {
    btnTypeList,
    BUTTON_PATTERN_CONTROL,
    FilledInput,
    FillPickerPopoverDropdown,
    getInputInnerTypeMatch,
    getIsFindUseInRichText,
    getViewVariableOption,
    ListItem4ByecodeUi,
    NodeFieldPreview,
    PURE_ALLOW_ICON_TYPES,
    useAtomData,
    useFindUseObjectContext
} from '@lighthouse/shared'
import React, { useCallback, useMemo } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import styled from 'styled-components'

import { lastPageOfStackAtom } from '@/atoms/page/state'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useColorSystemAction } from '@/hooks/useColorSystemAction'
import { useCustomViewStatisticExtraFields } from '@/hooks/useCustomViewStatisticExtraFields'
import { useDataSource, useDataSourceList } from '@/hooks/useDataSource'
import { useGetColorVariableOptions } from '@/hooks/useGetColorVariableOptions'
import { useIsDisabledWithVersion } from '@/hooks/useIsDisabledWithVersion'
import { useGetPageParameterAndVariableOptions, useGetUserRecordOptions } from '@/hooks/usePage'
import { useVariableCustomViewOption } from '@/hooks/useVariableCustomViewOption'

import FormSegmentedControl from '../Common/FormSegmentedControl'
import { IconControl } from '../Common/IconControl'
import * as CM from '../styles'
import { FIT_TYPE_CONTROL, ICON_POSITION_CONTROL } from './constant'
import * as SC from './styles'

const SCxListItemBaseInfo = styled.div`
    gap: 0;
    padding: 0 16px;
`

interface ButtonItemConfigureProps {
    disableViewOption?: boolean
    prefixName?: string
    listKeyName?: string
    index?: number
    hideConfigs?: {
        fillWay?: boolean
    }
    style?: React.CSSProperties
}

export const ButtonItemConfigure: React.FunctionComponent<ButtonItemConfigureProps> = ({
    index,
    prefixName = '',
    hideConfigs,
    style,
    disableViewOption
}) => {
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSourceList = useDataSourceList(appId, envId)
    const { control, watch } = useFormContext()
    const settingPrefix = index === undefined ? prefixName : `${prefixName}.${index}`
    const { icon, showType, iconPosition, fillWay, backgroundColor, color, borderColor, pattern } = watch(settingPrefix)

    const [pointer, viewType] = useWatch({
        control,
        name: ['pointer', 'viewType']
    })

    const pointerDataSource = dataSourceList.find(ds => ds.id === pointer)
    const disabledWithVersion = useIsDisabledWithVersion()

    const findUseObject = useFindUseObjectContext()
    const [stackId, pageId] = useAtomData(
        lastPageOfStackAtom,
        useCallback(s => [s?.stackId || '', s?.pageId || ''], [])
    )

    const pageOptions = useGetPageParameterAndVariableOptions({ pageId })
    const userOption = useGetUserRecordOptions()

    const { customViewOption } = useVariableCustomViewOption()
    const extraViewFields = useCustomViewStatisticExtraFields()
    const viewOptions = useMemo(() => {
        const options: VariableTree2[] = []
        if (!disableViewOption && pointerDataSource) {
            const viewOption = getViewVariableOption({
                dataSource: pointerDataSource,
                viewType,
                extraFields: extraViewFields
            })
            if (viewOption) {
                options.push(viewOption)

                return options
            }
        }

        if (customViewOption) {
            options.push(customViewOption)
        }

        return options
    }, [customViewOption, disableViewOption, extraViewFields, pointerDataSource, viewType])

    const colorSystemMethods = useColorSystemAction()

    const variableTypeOption = useGetColorVariableOptions()
    return (
        <SCxListItemBaseInfo style={style}>
            <ListItem4ByecodeUi
                alignItems="center"
                justifyContent="space-between"
                styles={{
                    root: {
                        gap: 0
                    }
                }}
            >
                <Text color="var(--color-gray-600)">标题</Text>
                <SC.RightFill>
                    <Controller
                        control={control}
                        name={`${settingPrefix}.name`}
                        render={({ field }) => {
                            const isHighLight = getIsFindUseInRichText({
                                doc: field.value,
                                findUseObject
                            })
                            return (
                                <NodeFieldPreview
                                    innerTypes={getInputInnerTypeMatch('TEXT')}
                                    disabled={disabledWithVersion}
                                    highlighting={isHighLight}
                                    style={{ width: 180 }}
                                    autoHeight
                                    placeholder="输入按钮名称"
                                    viewOptions={viewOptions}
                                    pageOptions={pageOptions}
                                    userOption={userOption}
                                    value={field.value}
                                    dataSourceList={dataSourceList}
                                    onChange={field.onChange}
                                    onBlur={field.onChange}
                                />
                            )
                        }}
                    />
                </SC.RightFill>
            </ListItem4ByecodeUi>
            <ListItem4ByecodeUi alignItems="center" justifyContent="space-between">
                <Text>按钮元素</Text>
                <SC.RightFill>
                    <Controller
                        name={`${settingPrefix}.showType`}
                        control={control}
                        defaultValue={showType}
                        render={({ field }) => (
                            <CM.BlockSettingsSelect
                                // withinPortal
                                searchable={false}
                                // rightSection={<IconFont type="ArrowDownSmall" />}
                                name="pointer"
                                defaultValue="icon-name"
                                value={field.value}
                                options={btnTypeList}
                                style={{
                                    width: field.value === 'name' ? 180 : 140
                                }}
                                onChange={field.onChange}
                            />
                        )}
                    />
                    {showType !== 'name' && (
                        <Controller
                            name={`${settingPrefix}.icon`}
                            control={control}
                            defaultValue={icon}
                            render={({ field }) => (
                                <IconControl
                                    allowIconTypes={PURE_ALLOW_ICON_TYPES}
                                    value={field.value}
                                    onChange={field.onChange}
                                    clearable={false}
                                />
                            )}
                        />
                    )}
                </SC.RightFill>
            </ListItem4ByecodeUi>

            {showType !== 'name' && (
                <Controller
                    name={`${settingPrefix}.iconPosition`}
                    control={control}
                    defaultValue={iconPosition}
                    render={({ field }) => (
                        <FormSegmentedControl label="图标位置" data={ICON_POSITION_CONTROL} value={field.value} onChange={field.onChange} />
                    )}
                />
            )}
            {!hideConfigs?.fillWay && (
                <Controller
                    name={`${settingPrefix}.fillWay`}
                    control={control}
                    defaultValue={fillWay}
                    render={({ field }) => (
                        <FormSegmentedControl label="宽度规则" data={FIT_TYPE_CONTROL} value={field.value} onChange={field.onChange} />
                    )}
                />
            )}
            <Controller
                name={`${settingPrefix}.pattern`}
                control={control}
                defaultValue={pattern}
                render={({ field }) => (
                    <FormSegmentedControl
                        label="颜色样式"
                        data={BUTTON_PATTERN_CONTROL}
                        value={field.value}
                        size="sm"
                        onChange={field.onChange}
                    />
                )}
            />

            {pattern === ActionItemPattern.custom && (
                <>
                    <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                        <Text>背景色</Text>
                        <Controller
                            name={`${settingPrefix}.backgroundColor`}
                            control={control}
                            defaultValue={backgroundColor}
                            render={({ field }) => (
                                <Popover width={300} withinPortal position="left-start" autoUpdate={false} offsetOptions={152}>
                                    <Popover.Target>
                                        <FilledInput
                                            style={{ width: 180 }}
                                            variableTypeOption={variableTypeOption}
                                            value={field.value}
                                            onClear={() => field.onChange({ type: BACKGROUND_TYPE.color })}
                                        />
                                    </Popover.Target>
                                    <FillPickerPopoverDropdown
                                        title="填充"
                                        value={field.value}
                                        onChange={field.onChange}
                                        enabledBackgroundTypes={[BACKGROUND_TYPE.color, BACKGROUND_TYPE.gradient, BACKGROUND_TYPE.variable]}
                                        variableTypeOption={variableTypeOption}
                                        {...colorSystemMethods}
                                    />
                                </Popover>
                            )}
                        />
                    </ListItem4ByecodeUi>
                    <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                        <Text>图标色</Text>
                        <Controller
                            name={`${settingPrefix}.iconColor`}
                            control={control}
                            render={({ field }) => (
                                <Popover width={300} withinPortal position="left-start" autoUpdate={false} offsetOptions={152}>
                                    <Popover.Target>
                                        <FilledInput
                                            style={{ width: 180 }}
                                            value={field.value}
                                            onClear={() => field.onChange({ type: BACKGROUND_TYPE.color })}
                                        />
                                    </Popover.Target>
                                    <FillPickerPopoverDropdown
                                        title="填充"
                                        value={field.value}
                                        onChange={field.onChange}
                                        enabledBackgroundTypes={[BACKGROUND_TYPE.color]}
                                        {...colorSystemMethods}
                                    />
                                </Popover>
                            )}
                        />
                    </ListItem4ByecodeUi>
                    <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                        <Text>文字色</Text>
                        <Controller
                            name={`${settingPrefix}.color`}
                            control={control}
                            defaultValue={color}
                            render={({ field }) => (
                                <Popover width={300} withinPortal position="left-start" autoUpdate={false} offsetOptions={152}>
                                    <Popover.Target>
                                        <FilledInput
                                            style={{ width: 180 }}
                                            value={field.value}
                                            onClear={() => field.onChange({ type: BACKGROUND_TYPE.color })}
                                        />
                                    </Popover.Target>
                                    <FillPickerPopoverDropdown
                                        title="填充"
                                        value={field.value}
                                        onChange={field.onChange}
                                        enabledBackgroundTypes={[BACKGROUND_TYPE.color, BACKGROUND_TYPE.gradient]}
                                        {...colorSystemMethods}
                                    />
                                </Popover>
                            )}
                        />
                    </ListItem4ByecodeUi>
                    <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                        <Text>描边色</Text>
                        <Controller
                            name={`${settingPrefix}.borderColor`}
                            control={control}
                            defaultValue={borderColor}
                            render={({ field }) => (
                                <Popover width={300} withinPortal position="left-start" autoUpdate={false} offsetOptions={152}>
                                    <Popover.Target>
                                        <FilledInput
                                            style={{ width: 180 }}
                                            value={field.value}
                                            onClear={() => field.onChange({ type: BACKGROUND_TYPE.color })}
                                        />
                                    </Popover.Target>
                                    <FillPickerPopoverDropdown
                                        title="填充"
                                        value={field.value}
                                        onChange={field.onChange}
                                        enabledBackgroundTypes={[BACKGROUND_TYPE.color]}
                                        {...colorSystemMethods}
                                    />
                                </Popover>
                            )}
                        />
                    </ListItem4ByecodeUi>
                </>
            )}
        </SCxListItemBaseInfo>
    )
}
